/* ==========================================================================
   CasMarkt v2.1 — item 2(b), the reusable custom select.

   Owner: "when clicking on it and there's a dropdown it doesn't look as good
   — can you make a custom dropdown box?"

   This is a progressive enhancement, never a replacement. The native <select>
   stays in the DOM, inside the form, carrying its own name and value, so
   Spotlight's facets/sorting, the contact form's POST and every no-JS visitor
   keep working exactly as before. What changes is what is on screen: a pill
   trigger plus a card popover with role="listbox", and every commit writes the
   value back to the native element and dispatches a bubbling `change` so the
   listeners that were already there fire unchanged.

   Applies to `select.cm-select` and any `select` inside `[data-cm-select]`.
   The variant pickers are deliberately NOT in that set — v2.1 item 2(a) turns
   those into radio pills instead.

   Touch under 750px keeps the native control: phones render the OS picker
   sheet, which is better than anything a page can draw.
   ========================================================================== */
(function () {
  'use strict';

  var MATCH = 'select.cm-select, [data-cm-select] select';
  var TYPEAHEAD_MS = 700;
  var GAP = 6;
  var seq = 0;
  var current = null; // the open instance, at most one

  /* ------------------------------------------------------------ conditions */
  function coarseAndNarrow() {
    return (
      window.matchMedia('(max-width: 749px)').matches &&
      ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0)
    );
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------- utilities */
  function svgChevron() {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'cm-sel__chev');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'm4 6 4 4 4-4');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  function labelFor(select) {
    if (!select.id) return null;
    try {
      return document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(select.id) : select.id) + '"]');
    } catch (e) {
      return null;
    }
  }

  /* ==========================================================================
     One enhanced select.
     ========================================================================== */
  function CMSelect(select) {
    this.select = select;
    this.id = 'cmsel-' + (seq += 1);
    this.open = false;
    this.active = -1;
    this.buffer = '';
    this.bufferAt = 0;
    this.build();
  }

  CMSelect.prototype.build = function () {
    var self = this;
    var select = this.select;

    var host = document.createElement('span');
    host.className = 'cm-sel';
    var style = select.getAttribute('data-cm-select-style');
    if (style) host.className += ' cm-sel--' + style;

    // The native control moves inside the wrapper and is hidden by CSS; the
    // element it came out of is flagged so its old chevron can be hidden too.
    var parent = select.parentNode;
    parent.insertBefore(host, select);
    host.appendChild(select);
    select.classList.add('cm-sel__native');
    select.setAttribute('tabindex', '-1');
    parent.classList.add('cm-select-on');
    this.parent = parent;
    this.host = host;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cm-sel__btn';
    button.id = this.id + '-btn';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    if (select.disabled) button.disabled = true;

    var value = document.createElement('span');
    value.className = 'cm-sel__val';
    button.appendChild(value);
    button.appendChild(svgChevron());
    host.appendChild(button);

    var label = labelFor(select);
    if (label) {
      if (!label.id) label.id = this.id + '-label';
      button.setAttribute('aria-labelledby', label.id + ' ' + button.id);
    } else if (select.getAttribute('aria-label')) {
      button.setAttribute('aria-label', select.getAttribute('aria-label'));
    }

    var pop = document.createElement('div');
    pop.className = 'cm-sel__pop';
    pop.id = this.id + '-list';
    pop.setAttribute('role', 'listbox');
    pop.setAttribute('tabindex', '-1');
    pop.hidden = true;
    if (label && label.id) pop.setAttribute('aria-labelledby', label.id);
    host.appendChild(pop);

    this.button = button;
    this.value = value;
    this.pop = pop;

    button.addEventListener('click', function () {
      self.toggle();
    });
    button.addEventListener('keydown', function (event) {
      self.onButtonKey(event);
    });
    pop.addEventListener('keydown', function (event) {
      self.onListKey(event);
    });
    pop.addEventListener('click', function (event) {
      var opt = event.target.closest ? event.target.closest('.cm-sel__opt') : null;
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      self.commit(Number(opt.dataset.i));
    });
    // Something else changed the native value (a script, a form reset).
    select.addEventListener('change', function () {
      if (!self.internal) self.paint();
    });
    // The browser focuses the native control to anchor a validation bubble.
    select.addEventListener('focus', function () {
      self.host.classList.add('cm-sel--nativefocus');
    });
    select.addEventListener('blur', function () {
      self.host.classList.remove('cm-sel--nativefocus');
    });

    this.render();
    this.paint();
    select.setAttribute('data-cm-select-ready', 'true');
  };

  /* Mirror the native option list. Called on build and whenever the option set
     could have changed underneath us. */
  CMSelect.prototype.render = function () {
    var pop = this.pop;
    var options = this.select.options;
    pop.textContent = '';
    for (var i = 0; i < options.length; i += 1) {
      var option = options[i];
      var el = document.createElement('div');
      el.className = 'cm-sel__opt';
      el.id = this.id + '-o' + i;
      el.setAttribute('role', 'option');
      el.dataset.i = String(i);
      el.setAttribute('aria-selected', option.selected ? 'true' : 'false');
      if (option.disabled) {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('cm-sel__opt--muted');
      }
      var text = document.createElement('span');
      text.className = 'cm-sel__opt-text';
      text.textContent = option.textContent.trim();
      el.appendChild(text);
      pop.appendChild(el);
    }
  };

  /* Trigger label + selected marks, from whatever the native select says. */
  CMSelect.prototype.paint = function () {
    var index = this.select.selectedIndex;
    var option = index > -1 ? this.select.options[index] : null;
    this.value.textContent = option ? option.textContent.trim() : '';
    this.host.classList.toggle('cm-sel--placeholder', !!option && (option.disabled || option.value === ''));

    var items = this.pop.querySelectorAll('.cm-sel__opt');
    for (var i = 0; i < items.length; i += 1) {
      items[i].setAttribute('aria-selected', Number(items[i].dataset.i) === index ? 'true' : 'false');
    }
  };

  CMSelect.prototype.toggle = function () {
    if (this.open) this.close(true);
    else this.show();
  };

  CMSelect.prototype.show = function () {
    if (this.open || this.select.disabled) return;
    if (current && current !== this) current.close(false);

    this.render();
    this.paint();

    this.open = true;
    this.button.setAttribute('aria-expanded', 'true');
    this.button.setAttribute('aria-controls', this.pop.id);
    this.host.classList.add('cm-sel--open');

    // Out of the wrapper for the lifetime of the popover: the filter bar and
    // the contact card both clip their overflow, and a listbox that is cut off
    // is worse than a native one.
    document.body.appendChild(this.pop);
    this.pop.hidden = false;
    this.pop.classList.toggle('cm-sel__pop--still', reducedMotion());
    this.place();

    // Open on the current answer — unless that is a disabled placeholder, in
    // which case the first real option is where the keyboard should start.
    var at = this.select.selectedIndex;
    if (at < 0 || (this.select.options[at] && this.select.options[at].disabled)) at = this.firstEnabled();
    this.setActive(at);
    this.pop.focus();

    current = this;
    this.bindOutside();
    // Two frames so the starting transform is painted before it is removed.
    var pop = this.pop;
    window.requestAnimationFrame(function () {
      pop.classList.add('cm-sel__pop--in');
    });
  };

  CMSelect.prototype.close = function (focusBack) {
    if (!this.open) return;
    this.open = false;
    this.button.setAttribute('aria-expanded', 'false');
    this.button.removeAttribute('aria-controls');
    this.host.classList.remove('cm-sel--open');
    this.pop.classList.remove('cm-sel__pop--in');
    this.pop.hidden = true;
    this.pop.removeAttribute('aria-activedescendant');
    this.host.appendChild(this.pop); // back home, so it dies with the wrapper
    this.unbindOutside();
    if (current === this) current = null;
    if (focusBack) this.button.focus();
  };

  CMSelect.prototype.place = function () {
    var rect = this.button.getBoundingClientRect();
    var pop = this.pop;

    // Never narrower than the trigger, never so narrow that "Alphabetically,
    // A-Z" wraps: max-content gives the unwrapped width in one measurement.
    pop.style.width = 'max-content';
    var box = window.getComputedStyle(pop);
    var frame =
      parseFloat(box.paddingLeft) +
      parseFloat(box.paddingRight) +
      parseFloat(box.borderLeftWidth) +
      parseFloat(box.borderRightWidth);
    // The options are nowrap, so each row's scrollWidth is its true one-line
    // width whatever the box is currently doing. Safer than trusting how a
    // border-box element resolves `width: max-content`.
    var natural = pop.offsetWidth;
    var rows = pop.querySelectorAll('.cm-sel__opt');
    for (var r = 0; r < rows.length; r += 1) {
      natural = Math.max(natural, Math.ceil(rows[r].scrollWidth) + frame);
    }
    var width = Math.max(rect.width, Math.min(natural, 360));
    var room = window.innerWidth - 16;
    if (width > room) width = room;

    var left = rect.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - 8 - width;
    if (left < 8) left = 8;

    pop.style.width = width + 'px';
    pop.style.left = left + 'px';
    pop.style.maxHeight = 'none';

    // Below by default, above when the space under the trigger cannot hold it.
    var wanted = Math.min(pop.scrollHeight || 240, 320);
    var below = window.innerHeight - rect.bottom - GAP - 8;
    if (below < wanted && rect.top - GAP - 8 > below) {
      pop.style.top = 'auto';
      pop.style.bottom = window.innerHeight - rect.top + GAP + 'px';
      pop.style.maxHeight = Math.min(320, rect.top - GAP - 8) + 'px';
      pop.classList.add('cm-sel__pop--up');
    } else {
      pop.style.bottom = 'auto';
      pop.style.top = rect.bottom + GAP + 'px';
      pop.style.maxHeight = Math.min(320, Math.max(below, 120)) + 'px';
      pop.classList.remove('cm-sel__pop--up');
    }

    // A classic scrollbar only appears once max-height bites, and it eats into
    // the content box the max-content measurement above was taken without —
    // which is what made "Alphabetically, A-Z" wrap. Give that gutter back.
    var chrome = pop.offsetWidth - pop.clientWidth - 2; // less the 1px borders
    if (chrome > 0) pop.style.width = Math.min(width + chrome, room) + 'px';
  };

  CMSelect.prototype.bindOutside = function () {
    var self = this;
    this.onDown = function (event) {
      if (self.pop.contains(event.target) || self.host.contains(event.target)) return;
      self.close(false);
    };
    this.onMove = function () {
      self.place();
    };
    document.addEventListener('mousedown', this.onDown, true);
    document.addEventListener('touchstart', this.onDown, true);
    window.addEventListener('scroll', this.onMove, true);
    window.addEventListener('resize', this.onMove);
  };

  CMSelect.prototype.unbindOutside = function () {
    document.removeEventListener('mousedown', this.onDown, true);
    document.removeEventListener('touchstart', this.onDown, true);
    window.removeEventListener('scroll', this.onMove, true);
    window.removeEventListener('resize', this.onMove);
  };

  /* ------------------------------------------------------------- selection */
  CMSelect.prototype.items = function () {
    return this.pop.querySelectorAll('.cm-sel__opt');
  };

  CMSelect.prototype.firstEnabled = function () {
    var options = this.select.options;
    for (var i = 0; i < options.length; i += 1) if (!options[i].disabled) return i;
    return 0;
  };

  CMSelect.prototype.setActive = function (index) {
    var items = this.items();
    if (!items.length) return;
    if (index < 0) index = 0;
    if (index > items.length - 1) index = items.length - 1;
    this.active = index;
    for (var i = 0; i < items.length; i += 1) items[i].classList.toggle('cm-sel__opt--active', i === index);
    var el = items[index];
    if (!el) return;
    this.pop.setAttribute('aria-activedescendant', el.id);
    var top = el.offsetTop;
    var bottom = top + el.offsetHeight;
    if (top < this.pop.scrollTop) this.pop.scrollTop = top;
    else if (bottom > this.pop.scrollTop + this.pop.clientHeight) this.pop.scrollTop = bottom - this.pop.clientHeight;
  };

  CMSelect.prototype.step = function (delta) {
    var options = this.select.options;
    var index = this.active;
    for (var guard = 0; guard < options.length; guard += 1) {
      index += delta;
      if (index < 0 || index > options.length - 1) return;
      if (!options[index].disabled) {
        this.setActive(index);
        return;
      }
    }
  };

  CMSelect.prototype.commit = function (index) {
    var option = this.select.options[index];
    if (!option || option.disabled) return;
    var changed = this.select.selectedIndex !== index;
    this.select.selectedIndex = index;
    this.paint();
    this.close(true);
    if (!changed) return;
    this.internal = true;
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.internal = false;
  };

  /* ------------------------------------------------------------- keyboard */
  CMSelect.prototype.onButtonKey = function (event) {
    var key = event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
      event.preventDefault();
      this.show();
      if (key === 'ArrowDown') this.step(1);
      if (key === 'ArrowUp') this.step(-1);
    }
  };

  CMSelect.prototype.onListKey = function (event) {
    var key = event.key;
    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        this.step(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.step(-1);
        return;
      case 'Home':
        event.preventDefault();
        this.setActive(this.firstEnabled());
        return;
      case 'End':
        event.preventDefault();
        this.active = this.select.options.length;
        this.step(-1);
        return;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.commit(this.active);
        return;
      case 'Escape':
        event.preventDefault();
        this.close(true);
        return;
      // No preventDefault: focus goes back to the trigger and the browser's
      // own Tab then carries on from there, in either direction.
      case 'Tab':
        this.close(true);
        return;
      default:
        break;
    }
    if (key && key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      this.typeAhead(key);
    }
  };

  CMSelect.prototype.typeAhead = function (char) {
    var now = Date.now();
    this.buffer = now - this.bufferAt > TYPEAHEAD_MS ? char : this.buffer + char;
    this.bufferAt = now;

    var needle = this.buffer.toLowerCase();
    var options = this.select.options;
    for (var pass = 0; pass < 2; pass += 1) {
      // First pass looks after the active row so repeated letters cycle.
      var start = pass === 0 ? this.active + 1 : 0;
      var end = pass === 0 ? options.length : this.active + 1;
      for (var i = start; i < end; i += 1) {
        if (options[i].disabled) continue;
        if (options[i].textContent.trim().toLowerCase().indexOf(needle) === 0) {
          this.setActive(i);
          return;
        }
      }
    }
  };

  /* ==========================================================================
     Wiring
     ========================================================================== */
  function enhance(select) {
    if (!select || select.getAttribute('data-cm-select-ready') === 'true') return;
    if (select.closest('.cm-sel')) return;
    try {
      new CMSelect(select);
    } catch (e) {
      /* A broken enhancement must never take the native control with it. */
    }
  }

  function scan(root) {
    if (coarseAndNarrow()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var found = scope.querySelectorAll(MATCH);
    for (var i = 0; i < found.length; i += 1) enhance(found[i]);
  }

  /* Facets and search re-render whole sections, so the selects on the page are
     not the ones that were there a moment ago. One debounced observer covers
     every such swap without any section having to call in. */
  function watch() {
    if (typeof MutationObserver === 'undefined') return;
    var pending = null;
    new MutationObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        // A popover whose select was thrown away must go with it.
        var strays = document.body.querySelectorAll(':scope > .cm-sel__pop');
        for (var i = 0; i < strays.length; i += 1) {
          if (!current || current.pop !== strays[i]) strays[i].remove();
        }
        scan(document);
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    scan(document);
    watch();
    // Rotating a phone across the breakpoint should not leave a half-native,
    // half-custom row; anything not yet enhanced is picked up on the way up.
    window.addEventListener('resize', function () {
      if (!coarseAndNarrow()) scan(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.CMSelectEnhance = scan;
})();
